if (!customElements.get("progress-bar")) {
  customElements.define(
    "progress-bar",
    class ProgressBar extends HTMLElement {
      // État persisté par block (clé = id du wrapper #shopify-block-…) : un
      // static unique serait écrasé par la dernière instance connectée si
      // plusieurs barres de progression coexistent dans le tiroir.
      static _stateByBlock = new Map();
      // Panier connu vide : toute barre sans état capturé considère que le
      // total précédent était 0, pour que le premier ajout soit détecté
      // comme un franchissement de seuil.
      static _baselineZero = false;
      // Variantes récompense dont le POST /cart/add est en vol : évite un
      // second ajout si un re-render redéclenche la détection entre-temps.
      static _pendingVariantIds = new Set();

      _stateKey() {
        const wrapper = this.closest('[id^="shopify-block-"]');
        return wrapper ? wrapper.id : "progress-bar";
      }

      connectedCallback() {
        this.steps = this.querySelectorAll(".cart-drawer-progress-bar-advanced");
        this.wrapper = this.querySelector(".progress-bar__wrapper");
        this.dragEl = this.querySelector(".progress-bar__bar--drag");
        this.target = +this.dataset.target;
        this.cartTotal = +this.dataset.cartTotal || 0;
        this.allUnlockedMessage = this.dataset.allUnlockedMessage || "";
        this.titleEl = this.querySelector(".progress-bar__title");

        this.HORIZONTAL_BUFFER = 8;
        this._lastCartTotal = this.cartTotal;
        this._isFirstRun = true;

        if (!this.steps.length) return;

        const prev =
          this.constructor._stateByBlock.get(this._stateKey()) ||
          (this.constructor._baselineZero ? { cartTotal: 0, dragPercent: 0 } : null);
        this.addReachedRewards(prev ? prev.cartTotal : null, this.cartTotal);
        const animateOnConnect =
          !!prev && prev.cartTotal !== this.cartTotal && !!this.dragEl;

        if (animateOnConnect) {
          // The element is server-rendered with the NEW width and the
          // `.is-no-transition` class. We rewind it to the PREVIOUS width
          // without animation, flush layout, then drop the class so the
          // subsequent assignment to the new width plays the CSS transition.
          this._isInitialAnimating = true;

          this.dragEl.style.width = `${prev.dragPercent}%`;
          this.dragEl.offsetWidth;
          this.dragEl.classList.remove("is-no-transition");
          this._isFirstRun = false;

          this.updateTitle();
          this.applyMaxHeight();
          this.distributeAndFill({ animate: true });

          const endAnimation = () => {
            this._isInitialAnimating = false;
            if (this._animationFallbackTimer) {
              clearTimeout(this._animationFallbackTimer);
              this._animationFallbackTimer = null;
            }
            // La capture faite au connect a lieu avant la fin de la
            // transition : on re-capture ici la largeur réellement atteinte
            // pour que le prochain rembobinage parte de la bonne position.
            this._capturePersistedState();
          };
          this.dragEl.addEventListener("transitionend", endAnimation, { once: true });
          this._animationFallbackTimer = setTimeout(endAnimation, 900);
        } else {
          this.distributeAndFill({ animate: false });
          this.updateTitle();
          this.applyMaxHeight();

          requestAnimationFrame(() => {
            if (this.dragEl) this.dragEl.classList.remove("is-no-transition");
            this._isFirstRun = false;
          });
        }

        this._capturePersistedState();

        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        if (this.wrapper) this.resizeObserver.observe(this.wrapper);
        this.steps.forEach((step) => this.resizeObserver.observe(step));

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.scheduleLayout());
        }

        this.querySelectorAll(".cart-drawer-progress-bar-advanced img").forEach((img) => {
          if (!img.complete) {
            img.addEventListener("load", () => this.scheduleLayout(), { once: true });
          }
        });

        // Filet pour les mutations du panier qui ne re-rendent PAS le tiroir
        // (page /cart notamment) : le franchissement est alors détecté depuis
        // les données du panier publiées, sans reconnexion de la barre.
        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) =>
            this._onExternalCartUpdate(event)
          );
        }
      }

      _onExternalCartUpdate(event) {
        if (!event || event.source === "progress-bar-reward") return;
        const cart = event.cartData;
        // Seule la forme "panier complet" (réponses /cart/change|update) est
        // exploitable ; les réponses /cart/add (product-form) n'ont ni
        // total_price ni item_count, et ce chemin re-rend de toute façon le
        // tiroir (la détection se fait alors au reconnect).
        if (
          !cart ||
          typeof cart.total_price !== "number" ||
          typeof cart.item_count !== "number"
        ) {
          return;
        }

        const key = this._stateKey();
        const prevState =
          this.constructor._stateByBlock.get(key) ||
          (this.constructor._baselineZero ? { cartTotal: 0, dragPercent: 0 } : null);
        const prevTotal = prevState ? prevState.cartTotal : null;
        const newTotal = cart.total_price;
        if (prevTotal == null || newTotal === prevTotal) return;

        // La base est avancée AVANT la détection : le même franchissement ne
        // doit jamais pouvoir être re-détecté par l'événement suivant.
        this.constructor._stateByBlock.set(key, {
          cartTotal: newTotal,
          dragPercent: prevState.dragPercent,
        });

        // Dédup depuis les données publiées (le data-in-cart du DOM peut être
        // antérieur quand le tiroir n'a pas été re-rendu).
        this._externalCartItems = Array.isArray(cart.items) ? cart.items : null;
        try {
          this.addReachedRewards(prevTotal, newTotal);
        } finally {
          this._externalCartItems = null;
        }

        this.cartTotal = newTotal;
        this.dataset.cartTotal = newTotal;
        this.distributeAndFill({ animate: false });
        this.updateTitle();
        // Re-capture la largeur réellement appliquée pour que la prochaine
        // reconnexion animée reparte de la bonne position.
        this._capturePersistedState();
      }

      disconnectedCallback() {
        if (this._cartUpdateUnsubscriber) {
          this._cartUpdateUnsubscriber();
          this._cartUpdateUnsubscriber = null;
        }
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && cartDrawer.classList.contains("is-empty")) {
          // Panier vidé : oublier les totaux capturés et repartir d'une base
          // 0 pour que le premier ajout suivant soit traité comme un
          // franchissement et déclenche les récompenses des seuils atteints.
          this.constructor._stateByBlock.clear();
          this.constructor._baselineZero = true;
        } else {
          this._capturePersistedState();
        }
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this._rafId) cancelAnimationFrame(this._rafId);
        if (this._animationFallbackTimer) clearTimeout(this._animationFallbackTimer);
      }

      _capturePersistedState() {
        if (!this.dragEl) return;
        let pct = null;
        const barEl = this.dragEl.parentElement;
        const barWidth = barEl ? barEl.getBoundingClientRect().width : 0;
        if (barWidth > 0) {
          pct = (this.dragEl.getBoundingClientRect().width / barWidth) * 100;
        } else {
          // Élément détaché (swap innerHTML / replaceWith) : les rects valent
          // 0. On retombe sur la largeur cible posée en style inline, qui est
          // toujours exprimée en pourcentage.
          const styleWidth = parseFloat(this.dragEl.style.width);
          if (Number.isFinite(styleWidth)) pct = styleWidth;
        }
        if (pct == null || !Number.isFinite(pct)) return;
        this.constructor._stateByBlock.set(this._stateKey(), {
          cartTotal: this.cartTotal,
          dragPercent: Math.max(0, Math.min(100, pct)),
        });
      }

      scheduleLayout() {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
          // While the mount-time transition is running, animate:true keeps
          // setBarWidth from temporarily adding `.is-no-transition`, which
          // would snap the in-flight CSS transition to its end value.
          const animate = !!this._isInitialAnimating;
          this.distributeAndFill({ animate });
          this.applyMaxHeight();
        });
      }

      setBarWidth(pct, animate) {
        if (!this.dragEl) return;
        const clamped = Math.max(0, Math.min(100, pct));

        if (!animate) {
          this.dragEl.classList.add("is-no-transition");
          this.dragEl.style.width = `${clamped}%`;
          this.dragEl.offsetWidth;
          if (!this._isFirstRun) {
            this.dragEl.classList.remove("is-no-transition");
          }
        } else {
          this.dragEl.style.width = `${clamped}%`;
        }
      }

      distributeAndFill({ animate = false } = {}) {
        if (!this.steps.length || !this.wrapper) return;

        const wrapperWidth = this.wrapper.getBoundingClientRect().width;
        if (wrapperWidth <= 0) return;

        const targetCents = this.target * 100;
        if (targetCents <= 0) return;

        Array.from(this.steps).forEach((step) => {
          step.style.maxWidth = "";
        });

        const stepData = Array.from(this.steps)
          .map((el) => {
            const dataEl = el.querySelector(".progress-bar__step-data");
            const amount = dataEl ? +dataEl.dataset.amount : 0;
            return { el, amount, width: el.offsetWidth };
          })
          .filter((s) => s.amount > 0)
          .sort((a, b) => a.amount - b.amount);

        if (!stepData.length) return;

        const positions = stepData.map((s) =>
          Math.min((s.amount / targetCents) * wrapperWidth, wrapperWidth)
        );

        for (let i = 1; i < positions.length; i++) {
          const minPos =
            positions[i - 1] +
            (stepData[i - 1].width + stepData[i].width) / 2 +
            this.HORIZONTAL_BUFFER;
          if (positions[i] < minPos) positions[i] = minPos;
        }

        const lastIdx = positions.length - 1;
        const rightEdge = wrapperWidth - stepData[lastIdx].width / 2;
        if (positions[lastIdx] > rightEdge) {
          positions[lastIdx] = rightEdge;
          for (let i = lastIdx - 1; i >= 0; i--) {
            const maxAllowed =
              positions[i + 1] -
              (stepData[i].width + stepData[i + 1].width) / 2 -
              this.HORIZONTAL_BUFFER;
            if (positions[i] > maxAllowed) positions[i] = maxAllowed;
          }
        }

        if (positions[0] < stepData[0].width / 2) {
          positions[0] = stepData[0].width / 2;
          for (let i = 1; i < positions.length; i++) {
            const minPos =
              positions[i - 1] +
              (stepData[i - 1].width + stepData[i].width) / 2 +
              this.HORIZONTAL_BUFFER;
            if (positions[i] < minPos) positions[i] = minPos;
          }
        }

        stepData.forEach((s, i) => {
          const pct = (positions[i] / wrapperWidth) * 100;
          s.el.style.left = `${pct}%`;
          s.visualPct = pct;
        });

        this.updateBarFill(stepData, targetCents, animate);
      }

      updateBarFill(stepData, targetCents, animate) {
        if (!this.dragEl) return;

        const lastAmount = stepData[stepData.length - 1].amount;

        if (this.cartTotal >= lastAmount) {
          this.setBarWidth(100, animate);
          return;
        }

        const points = [{ amount: 0, pct: 0 }];
        stepData.forEach((s) => points.push({ amount: s.amount, pct: s.visualPct }));
        if (lastAmount < targetCents) {
          points.push({ amount: targetCents, pct: 100 });
        }

        let pct = 0;
        for (let i = 0; i < points.length - 1; i++) {
          if (this.cartTotal >= points[i].amount && this.cartTotal <= points[i + 1].amount) {
            const span = points[i + 1].amount - points[i].amount;
            const t = span > 0 ? (this.cartTotal - points[i].amount) / span : 0;
            pct = points[i].pct + t * (points[i + 1].pct - points[i].pct);
            break;
          }
        }

        this.setBarWidth(pct, animate);
      }

      updateTitle() {
        if (!this.titleEl) return;

        const stepsData = Array.from(this.steps)
          .map((step) => {
            const data = step.querySelector(".progress-bar__step-data");
            if (!data) return null;
            return {
              amount: +(data.dataset.amount || 0),
              remaining: data.dataset.remaining || ""
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.amount - b.amount);

        let nextStep = null;
        stepsData.forEach((step) => {
          if (this.cartTotal < step.amount && !nextStep) {
            nextStep = step;
          }
        });

        if (nextStep) {
          this.titleEl.innerHTML = nextStep.remaining;
        } else if (stepsData.length > 0) {
          this.titleEl.innerHTML = this.allUnlockedMessage;
        }
      }

      applyMaxHeight() {
        if (!this.wrapper || !this.steps.length) return;

        const stepOffset = this.steps[0].offsetTop;
        const maxStepHeight = Math.max(
          ...Array.from(this.steps).map((el) => el.offsetHeight)
        );

        this.wrapper.style.height = `${stepOffset + maxStepHeight}px`;
      }

      addReachedRewards(prevTotal, newTotal) {
        // Ajoute au panier le produit configuré sur chaque étape qui vient
        // d'être atteinte : le total du panier passe sous le seuil -> au-dessus.
        // L'ajout se fait une seule fois, au moment du franchissement, et le
        // produit reste un article de panier normal (modifiable / supprimable).
        // Jamais en mode design : le marchand qui teste le tiroir dans
        // l'éditeur de thème modifierait réellement son propre panier.
        if (window.Shopify && window.Shopify.designMode) return;
        if (prevTotal == null || newTotal <= prevTotal) return;
        if (typeof window.routes === "undefined" || !window.routes.cart_add_url) return;
        if (typeof fetchConfig !== "function") return;

        const pending = this.constructor._pendingVariantIds;
        const externalItems = this._externalCartItems;
        const items = [];
        this.querySelectorAll(".progress-bar__step-product").forEach((node) => {
          const id = node.dataset.variantId;
          const threshold = parseInt(node.dataset.threshold, 10) || 0; // centimes
          let alreadyInCart = (parseInt(node.dataset.inCart, 10) || 0) > 0;
          // Détection hors re-render du tiroir : le DOM peut être antérieur,
          // les lignes du panier publiées font foi.
          if (!alreadyInCart && externalItems) {
            alreadyInCart = externalItems.some(
              (item) => String(item.variant_id || item.id) === id
            );
          }
          if (!id || threshold <= 0 || alreadyInCart) return;
          // Déjà collecté dans ce lot (même produit sur deux étapes franchies
          // d'un coup) ou POST encore en vol (autre instance, re-render
          // pendant la requête) : ne pas ajouter une seconde quantité.
          if (pending.has(id)) return;
          if (prevTotal < threshold && newTotal >= threshold) {
            pending.add(id);
            items.push({ id, quantity: 1 });
          }
        });
        if (!items.length) return;

        const body = JSON.stringify({
          items,
          // Sections re-rendues renvoyées avec la réponse : elles permettent
          // de rafraîchir tout le tiroir (articles, barre, totaux) en un seul
          // aller-retour, sans second GET après coup (le subscriber cartUpdate
          // de cart.js ignore la source "progress-bar-reward" pour le tiroir).
          sections: "cart-drawer-advanced,cart-icon-bubble",
          sections_url: window.location.pathname,
        });

        // Instantané du jeton de rendu : si un rendu plus récent du tiroir
        // survient pendant le vol du POST, sa réponse est périmée et ne doit
        // pas être appliquée telle quelle.
        const seqAtPost = window.__cartDrawerRenderSeq || 0;

        fetch(window.routes.cart_add_url, { ...fetchConfig(), body })
          .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
          .then(({ ok, data }) => {
            if (!ok) {
              // 422 typique : produit épuisé ou limite d'achat atteinte
              // depuis le rendu du tiroir. On ne casse rien côté client,
              // mais on laisse une trace exploitable.
              console.error(
                "[progress-bar] ajout de la récompense refusé :",
                (data && (data.description || data.message)) || data
              );
              return;
            }
            const isFresh = seqAtPost === (window.__cartDrawerRenderSeq || 0);
            const rendered = isFresh && this._renderRewardSections(data);
            if (!rendered) {
              // Soit le tiroir a été re-rendu pendant le vol du POST (nos
              // sections sont un instantané antérieur), soit la réponse ne
              // contenait pas de sections exploitables : on re-fetch l'état
              // frais pour ne jamais laisser le tiroir obsolète.
              this._refreshDrawerFromServer();
            }
            if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: "progress-bar-reward", cartData: data });
            }
          })
          .catch((e) => console.error("[progress-bar]", e))
          .finally(() => {
            items.forEach((item) => pending.delete(item.id));
          });
      }

      _renderRewardSections(data) {
        // Retourne true seulement si le tiroir a réellement été re-rendu ;
        // l'appelant retombe sinon sur un re-fetch de l'état serveur.
        const sections = data && data.sections;
        if (!sections || !sections["cart-drawer-advanced"]) return false;

        const doc = new DOMParser().parseFromString(
          sections["cart-drawer-advanced"],
          "text/html"
        );
        const drawerEl = document.querySelector("cart-drawer");
        const inner = drawerEl && drawerEl.querySelector(".drawer__inner");
        const newInner = doc.querySelector(".drawer__inner");
        if (!inner || !newInner) return false;

        // Invalide les rafraîchissements du tiroir (GET de onCartUpdate)
        // partis avant cet état : leur réponse serait périmée. Uniquement
        // maintenant que le swap est certain, pour ne pas tuer un GET
        // correctif si on avait dû abandonner.
        window.__cartDrawerRenderSeq = (window.__cartDrawerRenderSeq || 0) + 1;

        // La classe is-empty de l'hôte pilote le layout, le conteneur de
        // trapFocus à l'ouverture ET la remise à zéro de l'état persisté
        // dans le disconnectedCallback des <progress-bar> détruites par le
        // swap : elle doit être posée AVANT l'affectation innerHTML (même
        // ordre que CartItems.updateQuantity), d'après l'état serveur rendu.
        const newDrawerHost = doc.querySelector("cart-drawer");
        if (newDrawerHost) {
          drawerEl.classList.toggle(
            "is-empty",
            newDrawerHost.classList.contains("is-empty")
          );
        }

        const focusWasInside = inner.contains(document.activeElement);
        inner.innerHTML = newInner.innerHTML;

        // Le swap détruit l'élément focalisé s'il était dans le tiroir : sans
        // re-trap, le bouclage clavier du dialogue pointe des nœuds détachés.
        // On ne vole pas le focus s'il était ailleurs (saisie hors tiroir).
        if (
          focusWasInside &&
          drawerEl.classList.contains("active") &&
          typeof trapFocus === "function"
        ) {
          const container = document.getElementById("CartDrawer");
          if (container) trapFocus(container, inner);
        }

        const bubble = document.getElementById("cart-icon-bubble");
        if (bubble && sections["cart-icon-bubble"]) {
          const bubbleDoc = new DOMParser().parseFromString(
            sections["cart-icon-bubble"],
            "text/html"
          );
          const src = bubbleDoc.querySelector(".shopify-section");
          if (src) bubble.innerHTML = src.innerHTML;
        }
        return true;
      }

      _refreshDrawerFromServer(isRetry) {
        // Repli : re-rend le tiroir ET la pastille du panier depuis l'état
        // serveur courant, avec la discipline de jeton des GET (instantané au
        // départ, abandon si un rendu plus récent survient pendant le vol).
        // Utilisé quand le rendu direct des sections de la réponse /cart/add
        // est impossible ou périmé. Un unique retry couvre le throttling
        // (429) fréquent juste après un POST /cart/add.
        if (typeof window.routes === "undefined" || !window.routes.cart_url) return;
        const seq = window.__cartDrawerRenderSeq || 0;
        fetch(`${window.routes.cart_url}?sections=cart-drawer-advanced,cart-icon-bubble`)
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          })
          .then((sections) => {
            if (seq !== (window.__cartDrawerRenderSeq || 0)) return;
            this._renderRewardSections({ sections });
          })
          .catch((e) => {
            console.error("[progress-bar] rafraîchissement du tiroir impossible :", e);
            if (!isRetry) {
              setTimeout(() => this._refreshDrawerFromServer(true), 1000);
            }
          });
      }
    }
  );
}

// Panier vide au chargement de la page : aucune <progress-bar> n'est rendue
// (le tiroir affiche l'état "sans produit"), donc aucun connect ne capture de
// total de départ. On amorce la base à 0 pour que le premier ajout soit
// traité comme un franchissement depuis 0.
(() => {
  const drawer = document.querySelector("cart-drawer");
  const ProgressBarClass = customElements.get("progress-bar");
  if (drawer && drawer.classList.contains("is-empty") && ProgressBarClass) {
    ProgressBarClass._baselineZero = true;
  }
})();
